import { ReactNode, useCallback, useContext } from 'react'
import { Animation } from './Animation.tsx'
import { AppActionContext, AppSettings, AppState, models, useAppStateReducer } from './AppState.ts'
import { Camera } from './Camera.ts'
import { KeyboardHandler } from './KeyboardHandler.tsx'
import { valueForKeyPath } from './KeyPath.ts'
import { nullMannequin } from './Mannequin.tsx'
import { MeshView_opacitySteps } from './MeshView.tsx'
import { TimelineControls } from './Timeline/TimelineControls.tsx'
import { TimelineView } from './Timeline/TimelineView.tsx'
import { HStack, Padding, Spacer, VStack } from './UI.tsx'
import { Viewport } from './Viewport.tsx'

export function AppView() {
  const [appState, sendAppAction] = useAppStateReducer()
  const { timeline, playheadTime } = appState.settings

  return <AppActionContext.Provider value={sendAppAction}>
    <VStack style={{
      overflow: 'auto',
      flexGrow: 1,
    }}>
      <KeyboardHandler />
      <PlaybackAnimation isPaused={appState.playTimeBase === null} />

      <TopBar
        settings={appState.settings}
        loadingText={loadingText(appState)}
      />

      {false && <DebugImage image={appState.mannequin.mesh.textureMap?.image ?? null} />}


      <Viewport appState={appState} />

      <TimelineView timeline={timeline} playheadTime={playheadTime} />

      <TimelineControls
        canGoToPriorKeyframe={playheadTime > 0}
        canGoToNextKeyframe={playheadTime < timeline.duration}
        isPlaying={appState.playTimeBase !== null}
        canDeleteKeyframe={timeline.canDeleteKeyframeAtTime(playheadTime)}
      />

      <Spacer height='0px' />
    </VStack>
  </AppActionContext.Provider>
}

function DebugImage(props: {
  image: HTMLImageElement | null
}): ReactNode {
  if (!props.image) {
    return null
  }

  return <div ref={ref => { ref?.appendChild(props.image as Node) }}></div>
}

function loadingText(state: AppState): string {
  if (state.loadingError) {
    return state.loadingError.message
  }

  if (state.mannequin === nullMannequin) {
    return 'Loading…'
  }

  return 'Loaded.'
}

function TopBar(props: {
  settings: AppSettings
  loadingText: string
}): ReactNode {
  const { settings, loadingText } = props

  const sendAppAction = useContext(AppActionContext)

  function SettingsToggle(
    label: string,
    keyPath: string[],
    tooltip: string = ''
  ): ReactNode {
    return <label title={tooltip}>
      <input
        type='checkbox'
        checked={valueForKeyPath(keyPath, settings) as boolean}
        onChange={event => sendAppAction({
          type: 'setBoolSetting',
          keyPath,
          value: event.target.checked
        })} />
      {label}
    </label>
  }

  return <div>
    <Padding all='8px'>
      <VStack>
        {false && <CameraReadout camera={settings.viewport.camera} />}

        <HStack>
          <OpacitySlider opacity={settings.viewport.meshView.opacity} />
          {SettingsToggle('Reduce Shimmer', ['viewport', 'meshView', 'antiShimmerEnabled'], 'Try turning this on if transparency causes the mesh to shimmer. Helpful on high-DPI, high-refresh-rate displays.')}
          {SettingsToggle('Wireframe', ['viewport', 'meshView', 'wireframe'])}
          <Spacer />
          {SettingsToggle('World Axes', ['viewport', 'worldAxesEnabled'])}
          {SettingsToggle('Bones', ['viewport', 'bonesEnabled'])}
          {SettingsToggle('Floor', ['viewport', 'floorEnabled'])}
        </HStack>

        <HStack>
          <ModelPicker selectedModel={settings.selectedModel} loadingText={loadingText} />
          <Spacer />
          <input
            type='button'
            value='Reset Pose'
            onClick={() => sendAppAction({ type: 'resetPose' })}
          />
          <input
            type='button'
            value='Reset Camera'
            onClick={() => sendAppAction({ type: 'resetCamera' })}
          />
        </HStack>
      </VStack>
    </Padding>
  </div>
}

function OpacitySlider(props: {
  opacity: number
}): ReactNode {
  const { opacity } = props

  const sendAppAction = useContext(AppActionContext)

  return <label>
    Mesh Opacity:
    <input
      type='range'
      min={0.0} max={1.0} step={1.0 / MeshView_opacitySteps}
      list='opacityTicks'
      value={opacity}
      onChange={event => sendAppAction({
        type: 'setOpacity',
        opacity: event.target.valueAsNumber
      })}
    />
    <datalist id='opacityTicks'>
      <option value='0.25' />
      <option value='0.5' />
      <option value='0.75' />
    </datalist>
  </label>
}

function CameraReadout(props: {
  camera: Camera
}): ReactNode {
  const { camera } = props
  return <VStack>
    <HStack>{camera.position.debugDescription}</HStack>
    <HStack>∠{(camera.altitude * 180 / Math.PI).toFixed(7)}° ↺{(camera.azimuth * 180 / Math.PI).toFixed(7)}°</HStack>
  </VStack>
}

function ModelPicker(props: {
  selectedModel: string
  loadingText: string
}): ReactNode {
  const { selectedModel, loadingText } = props

  const sendAppAction = useContext(AppActionContext)

  return <HStack>
    <select
      value={selectedModel}
      onChange={event => sendAppAction({ type: 'pickModel', value: event.target.value })}
    >
      {models.map(model =>
        <option key={model} value={model}>
          {model}
        </option>
      )}
    </select>
    {loadingText}
  </HStack>
}

function PlaybackAnimation(props: {
  isPaused: boolean
}): ReactNode {

  const sendAppAction = useContext(AppActionContext)
  const step = useCallback(() => {
    sendAppAction({ type: 'animatePlayheadTime' })
  }, [sendAppAction])

  return props.isPaused ? null : <Animation step={step} />
}
